<template>
    <modal :name="name"
           height="100%"
           width="100%"
           clickToClose
           @before-open="beforeOpen"
           @before-close="beforeClose">
        <div class="scrollable h-100">
            <div class="row mx-0 border-bottom p-3">
                <div class="col-6 align-self-center font-18 text-semi-bold">
                    Upgrade your plan
                </div>
                <div class="col-6 align-self-center text-right">
                    <div class="help-text">
                        Any questions? We're happy to help!
                        <a @click.prevent="openChat" href="#" class="pr-3">
                            Talk to us!
                        </a>
                    </div>
                    <div @click="closeModal" class="close-btn pt-4">
                        <i class="fal fa-times"></i>
                    </div>
                </div>
            </div>
            <div v-if="isPlansFetching" class="font-24 text-muted text-center mt-5">
                Loading...
            </div>
            <div v-else-if="billingGroups.length === 0" class="text-center text-danger mt-5">
                Something went wrong
            </div>
            <template v-else>
                <div class="font-24 text-center mt-5">
                    What's the best plan for you?
                </div>
                <div v-if="currentPlanData && currentPlanData.group && currentPlanData.option"
                     class="font-14 text-center mt-3">
                    You're currently on the
                    <b>{{ currentPlanData.group.name }}</b>
                    for $0/month.
                    Your usage for this billing period is <b>{{ billingInfo.usage.invoices }}</b> invoices.
                </div>
                <div class="plans-container">
                    <div v-for="(group, idx) in billingGroups" :key="idx"
                         @click="selectedGroupIdx = idx"
                         class="plan-switch-btn"
                         :class="{ active: idx === selectedGroupIdx }">
                        <span v-if="group.icon" class="mr-2">
                            <i class="fa" :class="group.icon"></i>
                        </span>
                        {{ group.name }}
                    </div>
                    <div v-if="selectedGroupIdx !== -1" class="d-table text-center mt-3 mx-auto">
                        <div class="d-flex">
                            <BillingPlanCard
                                v-for="(plan, idx) in billingGroups[selectedGroupIdx].plans"
                                :key="plan.uuid"
                                @changed="onChangePlan"
                                :plan="plan"
                                :isFirst="idx === 0"
                                :getPlanById="getPlanById"
                                />
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </modal>
</template>

<script>
import axios from 'axios'
import {ERROR, SUCCESS} from '@/store/modules/ui/UI'
import BillingPlanCard from './parts/BillingPlanCard'
import {mapActions, mapGetters} from 'vuex'
import {BILLING_INFO_FETCH} from '@/store/modules/billing'
import {appHelpChat} from '@/services/chats'

export default {
    name: 'ChangeBillingPlan',
    components: {BillingPlanCard},
    props: {
        name: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            billingGroups: [],
            selectedGroupIdx: null,

            isPlansFetching: false,
            isPlanUpdating: false
        }
    },
    computed: {
        ...mapGetters('billing', [
            'billingInfo',
            'isExpiredCard'
        ]),

        currentPlanData() {
            let plan = null
            if (this.billingInfo.plan && this.billingInfo.plan.uuid) {
                plan = this.getPlanData(this.billingInfo.plan.uuid)
            }
            return plan // {group, option}
        }
    },
    methods: {
        ...mapActions('billing', {
            fetchBillingInfo: BILLING_INFO_FETCH
        }),

        beforeOpen() {
            this.fetchPlans()
        },

        beforeClose() {
        },

        updatePlan() {
            this.isPlanUpdating = true
            axios.post()
                .then(() => {
                    this.$snackbar.show({type: SUCCESS, text: 'Successfully updated'})
                    this.$modal.hide(this.name)
                })
                .catch(() => {
                    this.$snackbar.show({type: ERROR})
                })
                .finally(() => {
                    this.isPlanUpdating = false
                })
        },

        openChat() {
            appHelpChat.show()
        },

        fetchPlans() {
            this.isPlansFetching = true
            axios.get('teams/billing/plans')
                .then(res => {
                    this.billingGroups = res.data || []
                    if (this.billingGroups.length !== 0) {
                        const idx = this.billingGroups.findIndex(g => g.default)
                        this.selectedGroupIdx = idx === -1 ? 0 : idx
                    }
                })
                .finally(() => {
                    this.isPlansFetching = false
                })
        },

        getPlanById(uuid) {
            let plan = null
            for (const group of this.billingGroups) {
                if (group.plans) {
                    plan = group.plans.find(p => p.uuid === uuid)
                    if (plan) {
                        return plan
                    }
                }
            }
            return null
        },

        getPlanData(uuid) {
            let option = null
            for (const group of this.billingGroups) {
                if (group.plans) {
                    option = group.plans.map(g => g.options).flat(1).find(p => p.uuid === uuid)
                    if (option) {
                        return {group, option}
                    }
                }
            }
            return null
        },

        closeModal() {
            this.$modal.hide(this.name)
        },

        async onChangePlan() {
            this.closeModal()
            await this.fetchBillingInfo()
            if (this.isExpiredCard && this.billingInfo.plan && this.billingInfo.plan.uuid) {
                this.$modal.show('edit-billing-card')
            }
        }
    }
}
</script>

<style lang="scss" scoped>

.close-btn {
    line-height: 0;
    display: inline;
    font-size: 24px;
    padding: 8px;

    &:hover {
        cursor: pointer;
        color: $color-purple-primary;
    }
}

.help-text {
    height: 24px;
    display: inline-block;
    vertical-align: middle;
}

.plans-container {
    text-align: center;
    margin: 32px auto;
}

.plan-switch-btn {
    display: inline-block;
    width: 320px;
    margin: 8px;
    font-size: 16px;
    text-align: center;
    padding: 8px;
    border-bottom: 2px solid $color-grey-primary;

    &.active, &:hover {
        cursor: pointer;
        color: $color-purple-primary;
        border-color: $color-purple-primary;
    }
}
</style>
